// Companies and their document letterhead (ADR-031). A tenant has a handful
// of companies: one list request (all columns the screen shows), cached; edits
// go straight to the table (column grants + RLS: core.company.manage). The
// logo is a private Storage object under {tenant_id}/{company_id}/.

import { queryOptions } from "@tanstack/react-query";
import { isDemoMode, supabase } from "@/lib/supabase";

export interface Company {
  id: string;
  code: string;
  name: string;
  currency: string;
  country: string;
  timezone: string;
  is_active: boolean;
  legal_name: string | null;
  tax_id: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  logo_path: string | null;
}

export type CompanyLetterhead = Pick<
  Company,
  "name" | "legal_name" | "tax_id" | "address_line1" | "address_line2" | "postal_code" | "city" | "email" | "phone"
>;

const COLUMNS =
  "id,code,name,currency,country,timezone,is_active,legal_name,tax_id,address_line1,address_line2,postal_code,city,email,phone,logo_path";

export const companyKeys = {
  all: ["settings", "companies"] as const,
  logo: (path: string) => ["settings", "companies", "logo", path] as const,
};

const demo: Company[] = [
  {
    id: "demo-pl01",
    code: "PL01",
    name: "Zakład Łódź",
    currency: "PLN",
    country: "PL",
    timezone: "Europe/Warsaw",
    is_active: true,
    legal_name: "Demo Manufacturing Sp. z o.o.",
    tax_id: "PL1234567890",
    address_line1: "ul. Piotrkowska 120",
    address_line2: null,
    postal_code: "90-006",
    city: "Łódź",
    email: "biuro@demo.pl",
    phone: null,
    logo_path: null,
  },
];

export const companyListQuery = queryOptions({
  queryKey: companyKeys.all,
  queryFn: async ({ signal }): Promise<Company[]> => {
    if (isDemoMode || !supabase) return demo;
    const { data, error } = await supabase.from("companies").select(COLUMNS).order("code").abortSignal(signal);
    if (error) throw error;
    return data as Company[];
  },
  staleTime: 5 * 60_000,
});

export async function updateCompany(id: string, patch: Partial<CompanyLetterhead> & { logo_path?: string | null }): Promise<void> {
  if (isDemoMode || !supabase) return;
  const { data, error } = await supabase.from("companies").update(patch).eq("id", id).select("id");
  if (error) throw error;
  // RLS hides rows the user may not change: no row back means no permission.
  if (!data?.length) throw new Error("Missing permission core.company.manage");
}

const LOGO_TYPES: Record<string, string> = { "image/png": "png", "image/jpeg": "jpg" };
export const LOGO_MAX_BYTES = 512 * 1024;

export function logoProblem(file: File): "type" | "size" | null {
  if (!LOGO_TYPES[file.type]) return "type";
  if (file.size > LOGO_MAX_BYTES) return "size";
  return null;
}

/** Uploads a new logo object and points the company at it; the previous object is removed. */
export async function replaceLogo(tenantId: string, company: Company, file: File): Promise<void> {
  if (isDemoMode || !supabase) return;
  const path = `${tenantId}/${company.id}/logo-${Date.now()}.${LOGO_TYPES[file.type]}`;
  const up = await supabase.storage.from("company-logos").upload(path, file, { contentType: file.type, upsert: false });
  if (up.error) throw up.error;
  await updateCompany(company.id, { logo_path: path });
  if (company.logo_path) await supabase.storage.from("company-logos").remove([company.logo_path]);
}

export async function removeLogo(company: Company): Promise<void> {
  if (isDemoMode || !supabase || !company.logo_path) return;
  await updateCompany(company.id, { logo_path: null });
  await supabase.storage.from("company-logos").remove([company.logo_path]);
}

/** The logo as an object URL for the preview (private bucket: downloaded with the user's token). */
export function companyLogoQuery(path: string) {
  return queryOptions({
    queryKey: companyKeys.logo(path),
    queryFn: async (): Promise<string | null> => {
      if (isDemoMode || !supabase) return null;
      const { data, error } = await supabase.storage.from("company-logos").download(path);
      if (error) return null;
      return URL.createObjectURL(data);
    },
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
}

/** Outcome of load_sample_data: "already" when the tenant has it loaded. */
export type SampleDataResult = { ok: true; items: number; partners: number } | { ok: false; reason: "already" | "notEmpty" | "denied" | "failed" };

/** Creates the DEMO- data set in the company (one RPC, one transaction). */
export async function loadSampleData(companyId: string): Promise<SampleDataResult> {
  if (isDemoMode || !supabase) return { ok: false, reason: "failed" };
  const { data, error } = await supabase.rpc("load_sample_data", { p_company_id: companyId });
  if (error) return { ok: false, reason: error.code === "23505" ? "already" : error.code === "23514" ? "notEmpty" : error.code === "42501" ? "denied" : "failed" };
  const r = data as { items: number; partners: number };
  return { ok: true, items: r.items, partners: r.partners };
}
